import {
  AddBlockedDomainRequest,
  AddBlockedDomainResponse,
  AddEnterpriseDomainResponse,
  AddUsersToEnterpriseResponse,
  AdminApplyReferralRequest,
  AdminUpdateStripeCustomerIdRequest,
  AdminUpdateStripeCustomerIdResponse,
  AdminUserLookupResponse,
  AlgorithmId,
  AllEnterprisesResponse,
  BlockedDomainsListResponse,
  CampaignListItem,
  CampaignListResponse,
  CodeListItem,
  CodeListResponse,
  ConsensusPreviewRequest,
  ConsensusPreviewResponse,
  CreateCampaignRequest,
  CreateCodesRequest,
  CreateCodesResponse,
  CreateEnterpriseFromAdminRequest,
  CreateEnterprisePilotRequest,
  CreateSalesLedEnterpriseCustomBillingRequest,
  CreateSalesLedEnterpriseRequest,
  CreateSalesLedEnterpriseResponse,
  DirectorySyncPreview,
  DirectorySyncStartedResponse,
  DisbandEnterpriseRequest,
  DisbandEnterpriseResponse,
  DomainVerificationInitiateResponse,
  DomainVerificationStatus,
  EnterpriseBillingConfigCreate,
  EnterpriseBillingConfigListResponse,
  EnterpriseBillingConfigResponse,
  EnterpriseBillingConfigUpdate,
  EnterpriseDetailsResponse,
  EnterpriseMembersResponse,
  EnterprisePilotsResponse,
  EnterpriseRole,
  type GetEnterpriseMembersSortByEnum,
  type GetEnterpriseMembersSortDirEnum,
  InternalCostCenterProvisionPreviewResponse,
  InternalCostCenterProvisionRequest,
  InternalCostCenterProvisionResponse,
  type LiveReplayFold,
  type LiveReplayLine,
  type LiveReplayPerson,
  type LiveReplayResponse,
  type LiveReplaySlot,
  type MeetingAttributionGtDocumentResponsePrefillOriginEnum,
  type MeetingAttributionJob,
  type MeetingAttributionJobRerunResponse,
  type MeetingAttributionJobsResponse,
  type MeetingAttributionTurnPreferenceBreakdownRow,
  type MeetingAttributionTurnPreferenceResponse,
  type MeetingAttributionTurnPreferenceResponseReasonEnum,
  type MeetingAttributionTurnPreferenceResultsResponse,
  type MeetingAttributionTurnPreferenceTally,
  type MeetingAttributionTurnPreferenceTurn,
  type MeetingAttributionTurnPreferenceUnit,
  type MeetingAttributionTurnPreferenceVote,
  type MeetingAttributionTurnPreferenceVoteChoiceEnum,
  type MeetingAttributionTurnPreferenceVotesRequest,
  MeetingAudioUrlResponse,
  MeetingSpeakerMapResponse,
  PendingInvoiceResponse,
  type ProviderArtifactsResponse,
  PublicAPIPolicy,
  PublicDomainsListResponse,
  ReactivateEnterprisePilotRequest,
  RedemptionListResponse,
  type RelabelerArm,
  type RelabelerComparison,
  type RelabelerComparisonResponse,
  type RelabelerComparisonStatus,
  type RelabelerReplayReason,
  type RelabelerReplayResponse,
  type RelabelerRow,
  type RelabelerSidecarMode,
  type RelabelerStats,
  RemoveUsersFromEnterpriseResponse,
  SendInvoiceRequest,
  SendInvoiceResponse,
  SetEnterpriseSuperadminRequestActionEnum,
  SetEnterpriseSuperadminResponse,
  type ShippedAttributionResponse,
  type ShippedAttributionStatus,
  SyncDomainsToWorkosResponse,
  ToggleDomainDeletedResponse,
  UniversityDomainsListResponse,
  UpdateCampaignRequest,
  UpdateNotetakerEntitlementsRequest,
  UpdateNotetakerEntitlementsResponse,
  UpdatePilotEndResponse,
  UpdatePublicAPIPolicyRequest,
  UpdateUsersRoleResponse,
  UpdateUserTrialEndRequest,
  UpdateUserTrialEndResponse,
  WorkosDirectoryStatusResponse,
} from './generated'
import { fetchAPI, isApiError } from '.'

import { Enterprise, EnterpriseGates, EnterpriseSettings } from '@/types'

export type { GetEnterpriseMembersSortByEnum, GetEnterpriseMembersSortDirEnum }
export type { AdminUserLookupResponse }

export type FlowStateAudienceRequest = {
  reporting_month: string
  emails?: string[]
  domain?: string
  user_ids?: string[]
  all_users?: boolean
}

export type GenerateFlowStateRequest = FlowStateAudienceRequest & {
  min_word_count?: number
  min_app_count?: number | null
}

export type SendFlowStateRequest = FlowStateAudienceRequest & {
  dry_run?: boolean
  resend_failed?: boolean
}

export type GenerateAndSendFlowStateRequest = {
  reporting_month: string
  min_word_count?: number
  min_app_count?: number | null
  dry_run?: boolean
  resend_failed?: boolean
}

export type FlowStatePreviewRequest = Omit<FlowStateAudienceRequest, 'all_users'> & {
  limit?: number
  offset?: number
}

export type MarketingJobResponse = {
  job_id: string
  message: string
  log_query: string
}

export type MarketingJobStatusResponse = {
  id: string
  job_type: string
  reporting_month: string
  status: string
  total: number
  succeeded: number
  failed: number
  stage?: string | null
  started_at?: string | null
  completed_at?: string | null
  error?: string | null
}

export type FlowStateMonthSummaryResponse = {
  reporting_month: string
  generated_total: number
  pending_send_total: number
  sent_total: number
  failed_send_total: number
  sending_total: number
  skipped_total: number
  active_job?: MarketingJobStatusResponse | null
  latest_job?: MarketingJobStatusResponse | null
  can_send_existing: boolean
  can_regenerate_send: boolean
}

export type FlowStateProfilePreviewItem = {
  user_id: string
  email: string
  eligibility_word_count: number
  generated_at: string
  send_status: string
  send_attempts: number
  sent_at?: string | null
  send_error?: string | null
  profile: Record<string, unknown>
}

export type FlowStateProfilePreviewResponse = {
  total: number
  items: FlowStateProfilePreviewItem[]
}

export type BulkInput = { type: 'file'; file: File } | { type: 'emails'; emails: string[] }

// Internal admin enterprise creation

export async function createEnterpriseWithAdmin(data: CreateEnterpriseFromAdminRequest) {
  return fetchAPI<Enterprise>({
    method: 'POST',
    url: '/api/v1/internal/enterprises/create',
    data,
  })
}

export async function createEnterprisePilot(data: CreateEnterprisePilotRequest) {
  return fetchAPI<Enterprise>({
    method: 'POST',
    url: '/api/v1/internal/enterprises/create-pilot',
    data,
  })
}

// Internal admin enterprise management

export async function searchEnterprises(search: string) {
  return fetchAPI<AllEnterprisesResponse>({
    method: 'GET',
    url: `/api/v1/internal/enterprises?search=${encodeURIComponent(search)}`,
  })
}

export async function getPublicApiAccess(enterpriseId: string) {
  return fetchAPI<PublicAPIPolicy | null>({
    method: 'GET',
    url: `/api/v1/internal/enterprises/${enterpriseId}/public-api-access`,
  })
}

export async function updatePublicApiAccess(enterpriseId: string, request: UpdatePublicAPIPolicyRequest) {
  return fetchAPI<PublicAPIPolicy>({
    method: 'PUT',
    url: `/api/v1/internal/enterprises/${enterpriseId}/public-api-access`,
    data: request,
    expectedStatuses: [409],
  })
}

export async function getEnterpriseMembers(
  enterpriseId: string,
  params: {
    limit?: number
    offset?: number
    search?: string
    sort_by?: GetEnterpriseMembersSortByEnum
    sort_dir?: GetEnterpriseMembersSortDirEnum
    role?: EnterpriseRole
  } = {}
) {
  const searchParams = new URLSearchParams()
  if (params.limit !== undefined) searchParams.set('limit', String(params.limit))
  if (params.offset !== undefined) searchParams.set('offset', String(params.offset))
  // Trim whitespace and omit the param entirely when empty so we never emit
  // ``?search=``; mirrors the customer-portal ``teamMembersTable`` helper.
  const trimmedSearch = params.search?.trim()
  if (trimmedSearch) searchParams.set('search', trimmedSearch)
  if (params.sort_by !== undefined) searchParams.set('sort_by', params.sort_by)
  if (params.sort_dir !== undefined) searchParams.set('sort_dir', params.sort_dir)
  if (params.role !== undefined) searchParams.set('role', params.role)
  const queryString = searchParams.toString()

  return fetchAPI<EnterpriseMembersResponse>({
    method: 'GET',
    url: `/api/v1/internal/enterprises/${enterpriseId}/members${queryString ? `?${queryString}` : ''}`,
  })
}

export async function updateNotetakerEntitlements(enterpriseId: string, userIds: string[], entitled: boolean | null) {
  return fetchAPI<UpdateNotetakerEntitlementsResponse>({
    method: 'PATCH',
    url: `/api/v1/internal/enterprises/${encodeURIComponent(enterpriseId)}/notetaker-entitlements`,
    data: { user_ids: userIds, entitled } satisfies UpdateNotetakerEntitlementsRequest,
  })
}

export async function bulkAddUsersToEnterprise(
  enterpriseId: string,
  emails: string[],
  role: EnterpriseRole = 'member',
  createMissingUsers: boolean = false
) {
  return fetchAPI<AddUsersToEnterpriseResponse>({
    method: 'POST',
    url: `/api/v1/internal/enterprises/${enterpriseId}/members`,
    data: {
      users: emails.map((e) => ({ email: e })),
      role,
      create_missing_users: createMissingUsers,
    },
  })
}

export async function uploadUsersToEnterprise(enterpriseId: string, file: File, createMissingUsers: boolean = false) {
  const formData = new FormData()
  formData.append('file', file)

  const params = new URLSearchParams()
  if (createMissingUsers) params.set('create_missing_users', 'true')
  const queryString = params.toString()
  return fetchAPI<AddUsersToEnterpriseResponse>({
    method: 'POST',
    url: `/api/v1/internal/enterprises/${enterpriseId}/members/upload${queryString ? `?${queryString}` : ''}`,
    data: formData,
  })
}

export async function removeUsersFromEnterprise(enterpriseId: string, emails: string[]) {
  return fetchAPI<RemoveUsersFromEnterpriseResponse>({
    method: 'POST',
    url: `/api/v1/internal/enterprises/${enterpriseId}/members/remove`,
    data: { emails },
  })
}

export async function uploadRemoveUsersFromEnterprise(enterpriseId: string, file: File) {
  const formData = new FormData()
  formData.append('file', file)

  return fetchAPI<RemoveUsersFromEnterpriseResponse>({
    method: 'POST',
    url: `/api/v1/internal/enterprises/${enterpriseId}/members/remove/upload`,
    data: formData,
  })
}

// Billing config API functions

export async function listBillingConfigs() {
  return fetchAPI<EnterpriseBillingConfigListResponse>({
    method: 'GET',
    url: '/api/v1/internal/billing-configs',
  })
}

export async function createBillingConfig(data: EnterpriseBillingConfigCreate) {
  return fetchAPI<EnterpriseBillingConfigResponse>({
    method: 'POST',
    url: '/api/v1/internal/billing-configs/create',
    data,
  })
}

export async function updateBillingConfig(configId: string, data: EnterpriseBillingConfigUpdate) {
  return fetchAPI<EnterpriseBillingConfigResponse>({
    method: 'PATCH',
    url: `/api/v1/internal/billing-configs/${configId}`,
    data,
  })
}

export async function deleteBillingConfig(configId: string) {
  return fetchAPI<{ message: string }>({
    method: 'DELETE',
    url: `/api/v1/internal/billing-configs/${configId}`,
  })
}

// Blocked Domains

export async function getBlockedDomains() {
  return fetchAPI<BlockedDomainsListResponse>({
    method: 'GET',
    url: '/api/v1/internal/blocked-domains',
  })
}

export async function getPublicDomains() {
  return fetchAPI<PublicDomainsListResponse>({
    method: 'GET',
    url: '/api/v1/internal/public-domains',
  })
}

export async function getUniversityDomains() {
  return fetchAPI<UniversityDomainsListResponse>({
    method: 'GET',
    url: '/api/v1/internal/university-domains',
  })
}

export async function addBlockedDomain(data: AddBlockedDomainRequest) {
  return fetchAPI<AddBlockedDomainResponse>({
    method: 'POST',
    url: '/api/v1/internal/blocked-domains/create',
    data,
  })
}

export async function removeBlockedDomain(blockedDomainId: string) {
  return fetchAPI<{ message: string }>({
    method: 'DELETE',
    url: `/api/v1/internal/blocked-domains/${blockedDomainId}`,
  })
}

// Disband enterprise

export async function disbandEnterprise(enterpriseId: string, reason: string) {
  return fetchAPI<DisbandEnterpriseResponse>({
    method: 'POST',
    url: `/api/v1/internal/enterprises/${enterpriseId}/disband`,
    data: { reason } satisfies DisbandEnterpriseRequest,
  })
}

// Enterprise Details & Domains

export async function updateEnterpriseName(enterpriseId: string, name: string) {
  return fetchAPI<{ enterprise_id: string; name: string }>({
    method: 'PATCH',
    url: `/api/v1/internal/enterprises/${enterpriseId}/name`,
    data: { name },
  })
}

export async function getEnterpriseDetails(enterpriseId: string) {
  return fetchAPI<EnterpriseDetailsResponse>({
    method: 'GET',
    url: `/api/v1/internal/enterprise/${enterpriseId}`,
  })
}

export async function addEnterpriseDomain(enterpriseId: string, domain: string) {
  return fetchAPI<AddEnterpriseDomainResponse>({
    method: 'POST',
    url: '/api/v1/internal/add-enterprise-domain',
    data: { enterprise_id: enterpriseId, domain },
  })
}

export async function toggleDomainDeleted(enterpriseId: string, domainId: string, deleted: boolean) {
  return fetchAPI<ToggleDomainDeletedResponse>({
    method: 'PATCH',
    url: `/api/v1/internal/enterprises/${enterpriseId}/domains/${domainId}/toggle-deleted`,
    data: { deleted },
  })
}

export async function syncDomainsToWorkos(enterpriseId: string) {
  return fetchAPI<SyncDomainsToWorkosResponse>({
    method: 'POST',
    url: `/api/v1/internal/enterprises/${enterpriseId}/sync-workos`,
  })
}
// Role updates

export async function updateUserRole(enterpriseId: string, email: string, role: EnterpriseRole) {
  return fetchAPI<UpdateUsersRoleResponse>({
    method: 'PATCH',
    url: `/api/v1/internal/enterprises/${enterpriseId}/members/update-role`,
    data: { email, role },
  })
}

export async function setEnterpriseSuperadmin(
  enterpriseId: string,
  email: string,
  action: SetEnterpriseSuperadminRequestActionEnum
) {
  return fetchAPI<SetEnterpriseSuperadminResponse>({
    method: 'POST',
    url: `/api/v1/internal/enterprises/${enterpriseId}/members/superadmin`,
    data: { email, action },
  })
}

export async function uploadUpdateRoles(enterpriseId: string, file: File, role: EnterpriseRole) {
  const formData = new FormData()
  formData.append('file', file)

  return fetchAPI<UpdateUsersRoleResponse>({
    method: 'POST',
    url: `/api/v1/internal/enterprises/${enterpriseId}/members/update-role-bulk?role=${encodeURIComponent(role)}`,
    data: formData,
  })
}

// Enterprise Pilots

export type PilotStatusFilter = 'active' | 'expired' | undefined

export async function getEnterprisePilots(limit = 20, offset = 0, status?: PilotStatusFilter) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  })
  if (status) {
    params.append('status', status)
  }
  return fetchAPI<EnterprisePilotsResponse>({
    method: 'GET',
    url: `/api/v1/internal/enterprises/pilots?${params.toString()}`,
  })
}

export async function updateEnterprisePilotEnd(enterpriseId: string, pilotEnd: string, costCenterId: string) {
  return fetchAPI<UpdatePilotEndResponse>({
    method: 'PATCH',
    url: `/api/v1/internal/enterprises/${enterpriseId}/update-pilot-end`,
    data: { pilot_end: pilotEnd, cost_center_id: costCenterId },
  })
}

export async function reactivateEnterprisePilot(enterpriseId: string, trialEnd: string) {
  return fetchAPI<Enterprise>({
    method: 'POST',
    url: `/api/v1/internal/enterprises/${enterpriseId}/reactivate-pilot`,
    data: { trial_end: trialEnd } satisfies ReactivateEnterprisePilotRequest,
  })
}

// Sales-led enterprise creation

export async function createSalesLedEnterprise(data: CreateSalesLedEnterpriseRequest) {
  return fetchAPI<CreateSalesLedEnterpriseResponse>({
    method: 'POST',
    url: '/api/v1/internal/enterprises/create-sales-led',
    data,
  })
}

// Superadmin-only — uses a custom seat price that can breach contracted pricing.
export async function createSalesLedEnterpriseCustomBilling(data: CreateSalesLedEnterpriseCustomBillingRequest) {
  return fetchAPI<CreateSalesLedEnterpriseResponse>({
    method: 'POST',
    url: '/api/v1/internal/enterprises/create-sales-led-custom-billing',
    data,
  })
}

export async function getPendingInvoice(enterpriseId: string) {
  return fetchAPI<PendingInvoiceResponse>({
    method: 'GET',
    url: `/api/v1/internal/enterprises/${enterpriseId}/pending-invoice`,
  })
}

export async function sendEnterpriseInvoice(enterpriseId: string, data: SendInvoiceRequest) {
  return fetchAPI<SendInvoiceResponse>({
    method: 'POST',
    url: `/api/v1/internal/enterprises/${enterpriseId}/send-invoice`,
    data,
  })
}

// Cost center provisioning (superadmin-only; blocked under impersonation)

export async function previewCostCenterProvision(data: InternalCostCenterProvisionRequest) {
  return fetchAPI<InternalCostCenterProvisionPreviewResponse>({
    method: 'POST',
    url: '/api/v1/internal/cost-centers/preview-provision',
    data,
  })
}

export async function provisionCostCenter(data: InternalCostCenterProvisionRequest) {
  return fetchAPI<InternalCostCenterProvisionResponse>({
    method: 'POST',
    url: '/api/v1/internal/cost-centers/provision',
    data,
  })
}

// Preserve contracted seats

export async function updatePreserveContractedSeats(enterpriseId: string, preserveContractedSeats: boolean) {
  return fetchAPI<{ id: string; preserve_contracted_seats: boolean }>({
    method: 'PATCH',
    url: `/api/v1/internal/enterprises/${enterpriseId}/preserve-seats`,
    data: { preserve_contracted_seats: preserveContractedSeats },
  })
}

export async function getWorkosDirectoryStatus(enterpriseId: string) {
  return fetchAPI<WorkosDirectoryStatusResponse>({
    method: 'GET',
    url: `/api/v1/internal/enterprises/${enterpriseId}/workos-directory-status`,
  })
}

export async function updateWorkosDirectoryId(enterpriseId: string, workosDirectoryId: string | null) {
  return fetchAPI<{ id: string; workos_directory_id: string | null }>({
    method: 'PATCH',
    url: `/api/v1/internal/enterprises/${enterpriseId}/workos-directory-id`,
    data: { workos_directory_id: workosDirectoryId },
  })
}

export async function updateEnterpriseSettings(enterpriseId: string, settings: Partial<EnterpriseSettings>) {
  return fetchAPI<EnterpriseSettings>({
    method: 'PATCH',
    url: `/api/v1/internal/enterprises/${enterpriseId}/settings`,
    data: { settings },
  })
}

export async function updateEnterpriseGates(enterpriseId: string, gates: Partial<EnterpriseGates>) {
  return fetchAPI<EnterpriseSettings>({
    method: 'PATCH',
    url: `/api/v1/internal/enterprises/${enterpriseId}/gates`,
    data: { gates },
  })
}

// Directory Sync

export async function previewEnterpriseDirectorySync(enterpriseId: string) {
  return fetchAPI<DirectorySyncPreview>({
    method: 'GET',
    url: `/api/v1/internal/enterprises/${enterpriseId}/sync-directory-users/preview`,
  })
}

export async function syncEnterpriseDirectoryUsers(enterpriseId: string) {
  return fetchAPI<DirectorySyncStartedResponse>({
    method: 'POST',
    url: `/api/v1/internal/enterprises/${enterpriseId}/sync-directory-users`,
  })
}

// Promo Campaigns

export async function getPromoCampaigns(limit: number, offset: number) {
  return fetchAPI<CampaignListResponse>({
    method: 'GET',
    url: `/api/v1/internal/promo/campaigns?limit=${limit}&offset=${offset}`,
  })
}

export async function getPromoCampaign(campaignId: string) {
  return fetchAPI<CampaignListItem>({
    method: 'GET',
    url: `/api/v1/internal/promo/campaigns/${campaignId}`,
  })
}

export async function createPromoCampaign(campaignPayload: CreateCampaignRequest) {
  return fetchAPI<CampaignListItem>({
    method: 'POST',
    url: '/api/v1/internal/promo/campaigns',
    data: campaignPayload,
  })
}

export async function updatePromoCampaign(campaignId: string, campaignUpdate: UpdateCampaignRequest) {
  return fetchAPI<CampaignListItem>({
    method: 'PATCH',
    url: `/api/v1/internal/promo/campaigns/${campaignId}`,
    data: campaignUpdate,
  })
}

export async function getPromoCodes(campaignId: string, limit: number, offset: number) {
  return fetchAPI<CodeListResponse>({
    method: 'GET',
    url: `/api/v1/internal/promo/campaigns/${campaignId}/codes?limit=${limit}&offset=${offset}`,
  })
}

export async function exportPromoCodesCsv(campaignId: string) {
  return fetchAPI<Blob>({
    method: 'GET',
    url: `/api/v1/internal/promo/campaigns/${campaignId}/codes/export`,
    responseType: 'blob',
  })
}

export async function createPromoCodes(campaignId: string, codesPayload: CreateCodesRequest) {
  return fetchAPI<CreateCodesResponse>({
    method: 'POST',
    url: `/api/v1/internal/promo/campaigns/${campaignId}/codes`,
    data: codesPayload,
  })
}

export async function togglePromoCodeActive(codeId: string, isActive: boolean) {
  return fetchAPI<CodeListItem>({
    method: 'PATCH',
    url: `/api/v1/internal/promo/codes/${codeId}?is_active=${isActive}`,
  })
}

export async function getPromoRedemptions(limit: number, offset: number, campaignId?: string) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  })
  if (campaignId) params.append('campaign_id', campaignId)
  return fetchAPI<RedemptionListResponse>({
    method: 'GET',
    url: `/api/v1/internal/promo/redemptions?${params.toString()}`,
  })
}

export async function adminApplyReferral(data: AdminApplyReferralRequest) {
  return fetchAPI<{ message: string }>({
    method: 'POST',
    url: '/api/v1/internal/referral/apply',
    data,
  })
}

/** Support-team cap: max trial extension window from today. Mirrors backend MAX_USER_TRIAL_EXTENSION_DAYS. */
export const MAX_USER_TRIAL_EXTENSION_DAYS = 365

export type { UpdateUserTrialEndRequest, UpdateUserTrialEndResponse }

export async function updateUserTrialEnd(data: UpdateUserTrialEndRequest) {
  return fetchAPI<UpdateUserTrialEndResponse>({
    method: 'PATCH',
    url: '/api/v1/internal/users/trial-end',
    data,
  })
}

// DNS domain verification (internal superadmin)
//
// Response shapes come straight from the generated client
// (DomainVerificationStatus / DomainVerificationInitiateResponse). We keep
// thin wrappers here so callers can depend on stable fetchAPI-based helpers
// rather than the generated axios client.

export async function getInternalDomainVerificationStatus(domainId: string) {
  return fetchAPI<DomainVerificationStatus>({
    method: 'GET',
    url: `/api/v1/internal/domains/${encodeURIComponent(domainId)}/verification-status`,
  })
}

export async function initiateInternalDomainVerification(domainId: string) {
  return fetchAPI<DomainVerificationInitiateResponse>({
    method: 'POST',
    url: `/api/v1/internal/domains/${encodeURIComponent(domainId)}/initiate-verification`,
  })
}

export async function checkInternalDomainVerification(domainId: string) {
  return fetchAPI<DomainVerificationStatus>({
    method: 'POST',
    url: `/api/v1/internal/domains/${encodeURIComponent(domainId)}/check-verification`,
  })
}

export async function adminLookupUser(query: string) {
  return fetchAPI<AdminUserLookupResponse>({
    method: 'GET',
    url: `/api/v1/internal/users/lookup?q=${encodeURIComponent(query)}`,
  })
}

export type { AdminUpdateStripeCustomerIdRequest, AdminUpdateStripeCustomerIdResponse }

export async function updateStripeCustomerId(data: AdminUpdateStripeCustomerIdRequest) {
  return fetchAPI<AdminUpdateStripeCustomerIdResponse>({
    method: 'POST',
    url: '/api/v1/internal/users/stripe-customer-id',
    data,
  })
}

// Hand-written on purpose: these routes are excluded from the OpenAPI schema (admin portal only).
export interface AdminDictionaryImportResponse {
  rows_imported: number
  rows_skipped: number
}

// CSV export/import can exceed the global 20s axios timeout on large dictionaries.
const DICTIONARY_TRANSFER_TIMEOUT_MS = 60_000

export async function exportUserDictionaryCsv(userId: string) {
  return fetchAPI<Blob>({
    method: 'GET',
    url: `/api/v1/internal/users/${encodeURIComponent(userId)}/dictionary/export`,
    responseType: 'blob',
    timeout: DICTIONARY_TRANSFER_TIMEOUT_MS,
  })
}

/** `confirm` must repeat the target's email or user ID — the backend rejects a mismatch. */
export async function importUserDictionaryCsv(userId: string, file: File, confirm: string) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('confirm', confirm)

  return fetchAPI<AdminDictionaryImportResponse>({
    method: 'POST',
    url: `/api/v1/internal/users/${encodeURIComponent(userId)}/dictionary/import`,
    data: formData,
    timeout: DICTIONARY_TRANSFER_TIMEOUT_MS,
  })
}

// Marketing Flow State

export async function generateFlowStateProfiles(data: GenerateFlowStateRequest) {
  return fetchAPI<MarketingJobResponse>({
    method: 'POST',
    url: '/api/v1/internal/marketing/flow-state/generate',
    data,
  })
}

export async function generateAndSendFlowStateEmails(data: GenerateAndSendFlowStateRequest) {
  return fetchAPI<MarketingJobResponse>({
    method: 'POST',
    url: '/api/v1/internal/marketing/flow-state/generate-send',
    data,
  })
}

export async function sendFlowStateEmails(data: SendFlowStateRequest) {
  return fetchAPI<MarketingJobResponse>({
    method: 'POST',
    url: '/api/v1/internal/marketing/flow-state/send',
    data,
  })
}

export async function previewFlowStateProfiles(data: FlowStatePreviewRequest) {
  return fetchAPI<FlowStateProfilePreviewResponse>({
    method: 'POST',
    url: '/api/v1/internal/marketing/flow-state/preview',
    data,
  })
}

export async function getFlowStateJob(jobId: string) {
  return fetchAPI<MarketingJobStatusResponse>({
    method: 'GET',
    url: `/api/v1/internal/marketing/flow-state/jobs/${encodeURIComponent(jobId)}`,
  })
}

export async function getFlowStateMonthSummary(reportingMonth: string) {
  return fetchAPI<FlowStateMonthSummaryResponse>({
    method: 'GET',
    url: `/api/v1/internal/marketing/flow-state/months/${encodeURIComponent(reportingMonth)}`,
  })
}

export type {
  AlgorithmId,
  ConsensusPreviewResponse,
  LiveReplayFold,
  LiveReplayLine,
  LiveReplayPerson,
  LiveReplayResponse,
  LiveReplaySlot,
  MeetingAttributionJob,
  MeetingAttributionJobRerunResponse,
  MeetingAttributionJobsResponse,
  MeetingAudioUrlResponse,
  MeetingSpeakerMapResponse,
  RelabelerArm,
  RelabelerComparison,
  RelabelerComparisonResponse,
  RelabelerComparisonStatus,
  RelabelerReplayReason,
  RelabelerReplayResponse,
  RelabelerRow,
  RelabelerSidecarMode,
  RelabelerStats,
}

const getMeetingAttributionArtifact = (meetingId: string, artifact: string, signal?: AbortSignal) =>
  fetchAPI<string>({
    method: 'GET',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/${artifact}`,
    responseType: 'text',
    signal,
  })

export async function getMeetingAttributionJobs(meetingId: string, signal?: AbortSignal) {
  return fetchAPI<MeetingAttributionJobsResponse>({
    method: 'GET',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/jobs`,
    signal,
  })
}

export async function rerunMeetingAttributionJob(meetingId: string, sourceJobId: string, jobId: string) {
  return fetchAPI<MeetingAttributionJobRerunResponse>({
    method: 'POST',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/jobs/${encodeURIComponent(sourceJobId)}/rerun`,
    data: { job_id: jobId },
  })
}

export async function getMeetingAttributionLive(meetingId: string, signal?: AbortSignal) {
  return getMeetingAttributionArtifact(meetingId, 'live', signal)
}

export async function getMeetingAttributionRefined(meetingId: string, signal?: AbortSignal, jobId?: string | null) {
  const query = jobId == null ? '' : `?job_id=${encodeURIComponent(jobId)}`
  return getMeetingAttributionArtifact(meetingId, `refined${query}`, signal)
}

export async function getMeetingAttributionRaw(meetingId: string, signal?: AbortSignal) {
  return getMeetingAttributionArtifact(meetingId, 'refined-raw', signal)
}

export type AttributionProviderArtifacts = ProviderArtifactsResponse
export type AttributionProvider = 'soniox' | 'chorus'

export type { ShippedAttributionResponse, ShippedAttributionStatus }

export async function getMeetingAttributionShipped(meetingId: string, revision: string | null, signal?: AbortSignal) {
  return fetchAPI<ShippedAttributionResponse>({
    method: 'GET',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/shipped`,
    ...(revision ? { params: { revision } } : {}),
    signal,
  })
}

export async function getMeetingAttributionProviders(meetingId: string, revision: string | null, signal?: AbortSignal) {
  return fetchAPI<AttributionProviderArtifacts>({
    method: 'GET',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/provider-artifacts`,
    ...(revision ? { params: { expected_revision: revision } } : {}),
    signal,
    expectedStatuses: [409],
  })
}

export async function getMeetingAttributionGtRaw(
  meetingId: string,
  prefillVersion: string,
  rawSha256: string,
  signal?: AbortSignal
) {
  const query = new URLSearchParams({ expected_prefill_version: prefillVersion, expected_raw_sha256: rawSha256 })
  return getMeetingAttributionArtifact(meetingId, `ground-truth/raw?${query}`, signal)
}

export async function getMeetingAttributionObservations(meetingId: string, signal?: AbortSignal) {
  return getMeetingAttributionArtifact(meetingId, 'speaker-observations', signal)
}

export async function getMeetingAttributionSpeakerMap(meetingId: string, signal?: AbortSignal, jobId?: string | null) {
  return fetchAPI<MeetingSpeakerMapResponse>({
    method: 'GET',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/speaker-map`,
    ...(jobId == null ? {} : { params: { job_id: jobId } }),
    signal,
  })
}

export type TurnPreferenceChoice = MeetingAttributionTurnPreferenceVoteChoiceEnum
export type TurnPreferenceVote = MeetingAttributionTurnPreferenceVote
export type TurnPreferenceTurn = MeetingAttributionTurnPreferenceTurn
export type TurnPreferenceUnit = MeetingAttributionTurnPreferenceUnit
export type TurnPreferenceReason = MeetingAttributionTurnPreferenceResponseReasonEnum
export type TurnPreferenceResponse = MeetingAttributionTurnPreferenceResponse
export type TurnPreferenceTally = MeetingAttributionTurnPreferenceTally
export type TurnPreferenceBreakdownRow = MeetingAttributionTurnPreferenceBreakdownRow
export type TurnPreferenceResultsResponse = MeetingAttributionTurnPreferenceResultsResponse

export async function getMeetingTurnPreference(meetingId: string, signal?: AbortSignal) {
  return fetchAPI<TurnPreferenceResponse>({
    method: 'GET',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/turn-preference`,
    signal,
    // The server pairs both stored outputs under a 55 s budget; the default 20 s client timeout would abandon it.
    timeout: 60_000,
    expectedStatuses: [429, 503, 504],
  })
}

export async function saveMeetingTurnPreference(meetingId: string, votes: TurnPreferenceVote[], signal?: AbortSignal) {
  const body: MeetingAttributionTurnPreferenceVotesRequest = { votes }
  return fetchAPI<TurnPreferenceResponse>({
    method: 'POST',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/turn-preference`,
    data: body,
    signal,
    expectedStatuses: [409, 429, 503],
  })
}

export async function getMeetingAttributionTurnPreferenceResults(signal?: AbortSignal) {
  return fetchAPI<TurnPreferenceResultsResponse>({
    method: 'GET',
    url: '/api/v1/internal/meetings/attribution/turn-preference/results',
    signal,
  })
}

// Row-level votes from every round, decoded to provider, saved as-is for offline analysis.
export async function exportMeetingAttributionTurnPreferenceVotes() {
  return fetchAPI<Blob>({
    method: 'GET',
    url: '/api/v1/internal/meetings/attribution/turn-preference/export',
    responseType: 'blob',
    timeout: 60_000,
  })
}

export async function replayMeetingAttributionRelabeler(meetingId: string, signal?: AbortSignal) {
  return fetchAPI<RelabelerReplayResponse>({
    method: 'POST',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/relabeler/replay`,
    signal,
    // The server gives up at 55 s; the default 20 s client timeout would abandon a replay that still succeeds.
    timeout: 60_000,
    expectedStatuses: [429],
  })
}

export async function replayMeetingLiveSpeakers(meetingId: string, signal?: AbortSignal) {
  return fetchAPI<LiveReplayResponse>({
    method: 'POST',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/live-replay`,
    signal,
    // The fold gives up at 40 s after reads and a slot wait; the default 20 s client timeout would cut it short.
    timeout: 60_000,
    expectedStatuses: [429, 503],
  })
}

const getMeetingAttributionAudioUrlWithDisposition = (
  meetingId: string,
  download: boolean,
  signal?: AbortSignal,
  jobId?: string | null
) => {
  const params = new URLSearchParams()
  if (download) params.set('download', 'true')
  if (jobId != null) params.set('job_id', jobId)
  const queryString = params.toString()
  const query = queryString ? `?${queryString}` : ''
  return fetchAPI<MeetingAudioUrlResponse>({
    method: 'GET',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/audio-url${query}`,
    signal,
  })
}

export async function getMeetingAttributionAudioUrl(meetingId: string, signal?: AbortSignal, jobId?: string | null) {
  return getMeetingAttributionAudioUrlWithDisposition(meetingId, false, signal, jobId)
}

export async function getMeetingAttributionAudioDownloadUrl(
  meetingId: string,
  signal?: AbortSignal,
  jobId?: string | null
) {
  return getMeetingAttributionAudioUrlWithDisposition(meetingId, true, signal, jobId)
}

export async function previewMeetingSpeakerConsensus(
  meetingId: string,
  algorithmId: AlgorithmId,
  signal?: AbortSignal
) {
  const data: ConsensusPreviewRequest = { algorithm_id: algorithmId }
  return fetchAPI<ConsensusPreviewResponse>({
    method: 'POST',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/speaker-consensus/preview`,
    data,
    signal,
  })
}

// Strict gt_doc_v2 wire contracts. Assignability to the generated OpenAPI types is asserted in internal.attributionGt.test.ts.
export type GtStatus = 'not_started' | 'in_progress' | 'finalized' | 'finalized_with_edits'
export type PrefillOrigin = MeetingAttributionGtDocumentResponsePrefillOriginEnum

export type GtSpeakerKind = 'person' | 'room'

/** A shared device / conference room that one or more roster speakers (acoustic voices) belong to. */
export interface GtRoom {
  id: string
  label: string
}

/**
 * A `kind: 'room'` speaker is one acoustic voice of the room `room_id` points at; it keeps its own id,
 * name and source ids so scoring can still see which voice said what.
 */
export interface GtSpeaker {
  id: string
  name: string
  voice_name?: string | null
  hotkey: string | null
  is_recorder: boolean
  source_person_ids: string[]
  source_refined_speaker_ids: string[]
  kind: GtSpeakerKind
  room_id: string | null
}

export type GtTurnState = 'confirmed' | 'deleted' | null

export interface GtTurn {
  id: string
  start_ms: number
  end_ms: number
  text: string
  speaker_id: string | null
  state: GtTurnState
  note: string | null
  source_turn_id: string
  reviewed_by: string | null
  reviewed_at: string | null
}

export interface GtDocument {
  speakers: GtSpeaker[]
  turns: GtTurn[]
  rooms: GtRoom[]
}

export interface MeetingAttributionGtSnapshotTurnProduction {
  label: string
  person_ids: string[]
}

export interface MeetingAttributionGtSnapshotTurnHint {
  name: string
  why: string[]
}

export interface MeetingAttributionGtInputsPresent {
  observations: boolean
  live: boolean
}

export interface MeetingAttributionGtSnapshotTurn {
  turn_id: string
  start_ms: number
  end_ms: number
  text: string
  cluster: string
  production: MeetingAttributionGtSnapshotTurnProduction | null
  hint: MeetingAttributionGtSnapshotTurnHint | null
  inputs_present: MeetingAttributionGtInputsPresent
}

export interface MeetingAttributionGtDocumentResponse {
  meeting_id: string
  version: number
  status: GtStatus
  prefill_version: string
  prefill_origin: PrefillOrigin
  refined_raw_sha256: string
  turn_count: number
  reviewed_count: number
  turns_snapshot: MeetingAttributionGtSnapshotTurn[]
  document: GtDocument
  document_sha256: string
  reviewer_user_id: string | null
  created_at: string | null
  modified_at: string | null
}

export interface MeetingAttributionGtPrefillResponse {
  prefill_version: string
  refined_raw_sha256: string
  current_refined_raw_sha256: string | null
  annotatable: boolean
  annotatable_reason: string | null
  document: MeetingAttributionGtDocumentResponse
  status: GtStatus
  source: 'db' | 's3' | 'prefill'
  finalized_generation: number | null
  finalized_document_sha256: string | null
  hydrated_from_generation: number | null
  dropped_duplicate_turns: number
}

export interface MeetingAttributionGtSaveRequest {
  expected_version: number
  document: GtDocument
  prefill_version: string | null
  refined_raw_sha256: string | null
}

export interface MeetingAttributionGtSaveResponse {
  version: number
  document_sha256: string
  status: GtStatus
  turn_count: number
  reviewed_count: number
}

export type InvalidSaveReason =
  | 'duplicate_speaker_id'
  | 'duplicate_turn_id'
  | 'dangling_speaker_id'
  | 'unknown_source_turn_id'
  | 'invalid_time_range'
  | 'split_tiling'
  | 'confirmed_without_speaker'
  | 'duplicate_speaker_name'
  | 'multiple_recorders'
  | 'duplicate_hotkey'
  | 'duplicate_room_id'
  | 'dangling_room_id'
  | 'room_kind_mismatch'
  | 'duplicate_room_label'
  | 'empty_room'

export type FinalizeBlockerReason =
  'no_speaker' | 'undecided' | 'generic_speaker' | 'key_collision' | 'empty_key' | 'no_turns'

export interface MeetingAttributionGtRefusalResponse {
  reason: 'invalid_document' | 'incomplete_review' | 'stale_artifact'
  detail: InvalidSaveReason | FinalizeBlockerReason | null
  turn_id: string | null
  speaker_id: string | null
}

export type GtConflictReason =
  | 'stale_version'
  | 'conflict'
  | 'not_complete'
  | 'generation_conflict'
  | 'unchanged'
  | 'prefill_mismatch'
  | 'source_unavailable'
  | 'legacy_schema'

export interface MeetingAttributionGtConflictResponse {
  reason: GtConflictReason
  current_version: number | null
  document_sha256: string | null
  current_generation: number | null
  turn_count: number | null
  reviewed_count: number | null
}

export interface MeetingAttributionGtFinalizeRequest {
  expected_version: number
  base_generation: number | null
}

export interface MeetingAttributionGtFinalizeResponse {
  generation: number
  manifest_sha256: string
  s3_prefix: string
  row_deleted: boolean
  bytes_written: number
}

export interface MeetingAttributionGtDiscardResponse {
  version: number
}

export type GtKeywordCategory = 'name' | 'number' | 'term' | 'negation'

export interface GtKeyword {
  turn_id: string
  phrase: string
  category: GtKeywordCategory
  note: string | null
}

export interface MeetingAttributionGtKeywordTurn {
  turn_id: string
  speaker: string | null
  text: string
}

export interface MeetingAttributionGtKeywordsRequest {
  turns: MeetingAttributionGtKeywordTurn[]
}

export interface MeetingAttributionGtKeywordsResponse {
  keywords: GtKeyword[]
}

export async function getMeetingAttributionGtPrefill(meetingId: string, signal?: AbortSignal) {
  return fetchAPI<MeetingAttributionGtPrefillResponse>({
    method: 'GET',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/gt-prefill`,
    signal,
  })
}

export async function identifyMeetingAttributionGtKeywords(
  meetingId: string,
  body: MeetingAttributionGtKeywordsRequest,
  signal?: AbortSignal
) {
  return fetchAPI<MeetingAttributionGtKeywordsResponse>({
    method: 'POST',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/gt-keywords`,
    data: body,
    signal,
  })
}

export async function getMeetingAttributionGroundTruth(meetingId: string, signal?: AbortSignal) {
  return fetchAPI<MeetingAttributionGtDocumentResponse | null>({
    method: 'GET',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/ground-truth`,
    signal,
  })
}

export async function saveMeetingAttributionGroundTruth(
  meetingId: string,
  body: MeetingAttributionGtSaveRequest,
  signal?: AbortSignal
) {
  return fetchAPI<MeetingAttributionGtSaveResponse>({
    method: 'PUT',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/ground-truth`,
    data: body,
    signal,
    expectedStatuses: [400, 409],
  })
}

export async function finalizeMeetingAttributionGroundTruth(
  meetingId: string,
  body: MeetingAttributionGtFinalizeRequest,
  signal?: AbortSignal
) {
  return fetchAPI<MeetingAttributionGtFinalizeResponse>({
    method: 'POST',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/ground-truth/finalize`,
    data: body,
    signal,
    expectedStatuses: [400, 409],
  })
}

export async function discardMeetingAttributionGroundTruth(
  meetingId: string,
  expectedVersion: number,
  signal?: AbortSignal
) {
  return fetchAPI<MeetingAttributionGtDiscardResponse>({
    method: 'DELETE',
    url: `/api/v1/internal/meetings/${encodeURIComponent(meetingId)}/attribution/ground-truth?expected_version=${expectedVersion}`,
    signal,
    expectedStatuses: [404, 409],
  })
}

export type GtLeaderboardPlatform = 'google_meet' | 'zoom' | 'teams' | 'slack' | 'in_person' | 'other' | 'unknown'
export type GtLeaderboardOs = 'macos' | 'windows' | 'unknown'
export interface MeetingAttributionGtLeaderboardMeeting {
  meeting_id: string
  title: string | null
  created_at: string | null
  speaker_count: number | null
  duration_ms: number | null
  platform: GtLeaderboardPlatform
  os: GtLeaderboardOs
  reviewed_turns: number | null
  total_turns: number | null
}
export interface MeetingAttributionGtLeaderboardPerson {
  name: string
  completed: MeetingAttributionGtLeaderboardMeeting[]
  incomplete: MeetingAttributionGtLeaderboardMeeting[]
}
export interface MeetingAttributionGtLeaderboardPlatform {
  platform: GtLeaderboardPlatform
  annotated: number
  baseline: number
}
export interface MeetingAttributionGtLeaderboardResponse {
  generated_at: string
  completed_meetings: number
  started_meetings: number
  people: MeetingAttributionGtLeaderboardPerson[]
  platforms: MeetingAttributionGtLeaderboardPlatform[]
}

export async function getMeetingAttributionGtLeaderboard(signal?: AbortSignal) {
  return fetchAPI<MeetingAttributionGtLeaderboardResponse>({
    method: 'GET',
    url: '/api/v1/internal/meetings/attribution/ground-truth/leaderboard',
    signal,
  })
}

/** Must stay in sync with the refusal reason values in aria-web attribution_gt/service.py. */
const GT_REFUSAL_REASONS = new Set(['invalid_document', 'incomplete_review', 'stale_artifact'])
const GT_SAVE_CONFLICT_REASONS = new Set([
  'stale_version',
  'conflict',
  'not_complete',
  'generation_conflict',
  'unchanged',
  'prefill_mismatch',
  'source_unavailable',
])
const GT_FINALIZE_CONFLICT_REASONS = new Set([
  'stale_version',
  'conflict',
  'generation_conflict',
  'legacy_schema',
  'unchanged',
])

export function getGtConflict(error: unknown): MeetingAttributionGtConflictResponse | null {
  if (!isApiError(error) || error.status !== 409) return null
  const data = error.detailData
  if (!data || typeof data.reason !== 'string' || !GT_SAVE_CONFLICT_REASONS.has(data.reason)) return null
  return data as unknown as MeetingAttributionGtConflictResponse
}

export function getGtPrefillMismatch(error: unknown): MeetingAttributionGtConflictResponse | null {
  const conflict = getGtConflict(error)
  return conflict?.reason === 'prefill_mismatch' ? conflict : null
}

export function getGtFinalizeConflict(error: unknown): MeetingAttributionGtConflictResponse | null {
  if (!isApiError(error) || error.status !== 409) return null
  const data = error.detailData
  if (!data || typeof data.reason !== 'string' || !GT_FINALIZE_CONFLICT_REASONS.has(data.reason)) return null
  return data as unknown as MeetingAttributionGtConflictResponse
}

export function getGtRefusal(error: unknown): MeetingAttributionGtRefusalResponse | null {
  if (!isApiError(error) || (error.status !== 400 && error.status !== 409)) return null
  const data = error.detailData
  if (!data || typeof data.reason !== 'string' || !GT_REFUSAL_REASONS.has(data.reason)) return null
  return data as unknown as MeetingAttributionGtRefusalResponse
}
